import {
  ArrayMaxSize,
  ArrayMinSize,
  IsArray,
  IsIn,
  IsISO8601,
  IsOptional,
  IsString,
  IsUUID,
  MaxLength,
  MinLength,
  Validate,
  ValidatorConstraint,
  type ValidatorConstraintInterface,
} from 'class-validator';

/**
 * Chat request contracts, shared by gov-api and admin-api.
 *
 * In `contracts-common` rather than one portal's package because both APIs mount
 * the same routes against the same service — the portals differ in *who* they
 * will answer for, not in the shapes they accept.
 *
 * Response shapes are not restated here: they live in `@munifocus/chat`, whose
 * plain barrel is already free of the Nest/TypeORM runtime and so is safe for a
 * browser bundle to import.
 */

/** Kept in step with `MAX_BODY_LENGTH` in `@munifocus/chat`. */
const MAX_BODY = 8000;

/**
 * What may be shared into a conversation.
 *
 * Narrower than the document library's list, and deliberately so. This is
 * colleague-to-colleague messaging, not the evidence bucket: the useful cases
 * are a photo from the door, a scan, and a PDF someone has been sent. Anything
 * executable, archived, or of an unknown type is refused rather than stored —
 * an allowlist is the only version of this check that stays correct as new file
 * types appear.
 *
 * Here rather than in `@munifocus/chat` so the composer can apply the same rule
 * before it uploads. The server still enforces it — a browser check is a
 * courtesy, not a control — but a file the server will refuse should be refused
 * on the spot, with the reason, instead of after a pointless round trip.
 */
export const CHAT_ATTACHMENT_CONTENT_TYPES: readonly string[] = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'image/jpeg',
  'image/png',
  'image/heic',
  'image/webp',
  'image/tiff',
  'text/plain',
];

/**
 * Extensions matching {@link CHAT_ATTACHMENT_CONTENT_TYPES}, for the file
 * picker's `accept`.
 *
 * Both are listed because neither alone is reliable: some browsers report an
 * empty or `application/octet-stream` type for `.heic` and `.docx`, and an
 * extension-only filter misses a file that was renamed.
 */
export const CHAT_ATTACHMENT_ACCEPT = [
  ...CHAT_ATTACHMENT_CONTENT_TYPES,
  '.pdf',
  '.doc',
  '.docx',
  '.xlsx',
  '.jpg',
  '.jpeg',
  '.png',
  '.heic',
  '.webp',
  '.tif',
  '.tiff',
  '.txt',
].join(',');

/**
 * 15 MB. Smaller than the 25 MB document ceiling: a chat attachment is usually a
 * phone photo, and officers upload them over cellular from the field, where a
 * large file is a stalled send rather than a slow one.
 */
export const MAX_CHAT_ATTACHMENT_BYTES = 15 * 1024 * 1024;

/**
 * A media type reduced to the part the allowlist compares.
 *
 * Two things get stripped. **Parameters**, because a type is allowed to carry
 * them and the server's copy usually does: multer reads `file.mimetype` from the
 * multipart header, so a text file arrives as `text/plain; charset=utf-8` and a
 * bare string match would refuse it. **Case**, because media types are
 * case-insensitive by definition and only the browser's habit of sending them
 * lowercase was hiding that.
 */
function normalizeContentType(contentType: string): string {
  const [type] = contentType.split(';');
  return type.trim().toLowerCase();
}

/** True when a picked file is of a type a conversation will accept. */
export function isAcceptedChatContentType(contentType: string): boolean {
  return CHAT_ATTACHMENT_CONTENT_TYPES.includes(normalizeContentType(contentType));
}

/**
 * What the browser says when it could not identify a file at all.
 *
 * A missing or generic type is not a claim that the file is binary junk — it is
 * the absence of a claim. Chrome reports `application/octet-stream` for a `.docx`
 * or `.heic` often enough that treating it as a refusal made the attach button
 * look broken: the file vanished with "Upload failed" about six milliseconds
 * after it was picked, for a document type the allowlist plainly permits.
 */
const UNIDENTIFIED_CONTENT_TYPES = new Set(['', 'application/octet-stream', 'binary/octet-stream']);

/**
 * The canonical type for each extension we accept, used only to resolve the
 * unidentified case above.
 */
const EXTENSION_CONTENT_TYPES: Record<string, string> = {
  '.pdf': 'application/pdf',
  '.doc': 'application/msword',
  '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.png': 'image/png',
  '.heic': 'image/heic',
  '.webp': 'image/webp',
  '.tif': 'image/tiff',
  '.tiff': 'image/tiff',
  '.txt': 'text/plain',
};

/**
 * The type a shared file should be stored as, or null when it may not be shared.
 *
 * The reported type decides it whenever the browser actually recognised the
 * file. The extension is consulted *only* when it did not — never to override a
 * type the browser did report, since a `.pdf` that announces itself as
 * `application/zip` is exactly the case the allowlist exists to catch.
 *
 * This widens what is accepted but not what is stored: the return value is one
 * of {@link CHAT_ATTACHMENT_CONTENT_TYPES} or nothing — always normalized, never
 * the raw header — so an unidentified file is recorded under a real type rather
 * than as opaque bytes, and a parameterised one loses the parameter.
 */
export function resolveChatContentType(fileName: string, reportedType: string): string | null {
  const reported = normalizeContentType(reportedType);
  if (isAcceptedChatContentType(reported)) return reported;
  if (!UNIDENTIFIED_CONTENT_TYPES.has(reported)) return null;

  const dot = fileName.lastIndexOf('.');
  if (dot < 0) return null;
  return EXTENSION_CONTENT_TYPES[fileName.slice(dot).toLowerCase()] ?? null;
}

/**
 * One shared file, as the client describes it on send.
 *
 * An interface rather than a decorated class: nested class-validator needs
 * `class-transformer`'s `@Type`, which needs `reflect-metadata`, which this
 * contracts package — imported by browser bundles — has no business pulling in.
 * The shape is checked by {@link IsAttachmentArray} instead, matching how
 * `notifications.dto.ts` handles its nested window.
 */
export interface AttachmentDto {
  /**
   * The key returned by the upload endpoint. Accepted from the client because
   * the upload already proved they hold the bytes; the service signs it at read
   * time rather than trusting any URL the client might send instead.
   */
  storageKey: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
}

/** True when `value` is a well-formed {@link AttachmentDto}. */
export function isAttachmentShape(value: unknown): value is AttachmentDto {
  if (typeof value !== 'object' || value === null) return false;
  const { storageKey, fileName, contentType, sizeBytes } = value as Partial<AttachmentDto>;
  return (
    typeof storageKey === 'string' &&
    storageKey.length > 0 &&
    storageKey.length <= 512 &&
    typeof fileName === 'string' &&
    fileName.length > 0 &&
    fileName.length <= 255 &&
    typeof contentType === 'string' &&
    contentType.length > 0 &&
    contentType.length <= 128 &&
    typeof sizeBytes === 'number' &&
    Number.isInteger(sizeBytes) &&
    sizeBytes >= 0
  );
}

/**
 * Declared before the DTO that uses it: a `@Validate(...)` decorator is
 * evaluated when the class body is defined, so a constraint declared further
 * down the file is still in its temporal dead zone and throws on import.
 */
@ValidatorConstraint({ name: 'isAttachmentArray', async: false })
export class IsAttachmentArray implements ValidatorConstraintInterface {
  validate(value: unknown): boolean {
    return Array.isArray(value) && value.every(isAttachmentShape);
  }

  defaultMessage(): string {
    return 'each attachment needs a storageKey, fileName, contentType and a non-negative sizeBytes';
  }
}

export class SendMessageBody {
  /**
   * Optional so an attachment-only message validates; the service rejects the
   * case where both text and attachments are empty, since neither field alone
   * can express that rule.
   */
  @IsOptional()
  @IsString()
  @MaxLength(MAX_BODY)
  body?: string;

  /**
   * Client-minted idempotency key. Optional because a plain HTTP caller has no
   * reconnect to protect against; the portals always send one.
   */
  @IsOptional()
  @IsString()
  @MaxLength(64)
  clientKey?: string;

  @IsOptional()
  @IsArray()
  @ArrayMaxSize(10)
  @Validate(IsAttachmentArray)
  attachments?: AttachmentDto[];

  /**
   * Colleagues the message @mentions, by id. Honoured in a file's discussion
   * only, and only for people who can read that file; the rest are dropped.
   */
  @IsOptional()
  @IsArray()
  @ArrayMaxSize(20)
  @IsUUID('all', { each: true })
  mentionUserIds?: string[];
}

export class EditMessageBody {
  @IsString()
  @MinLength(1)
  @MaxLength(MAX_BODY)
  body!: string;
}

export class CreateDirectBody {
  @IsUUID()
  userId!: string;
}

export class CreateGroupBody {
  @IsString()
  @MinLength(1)
  @MaxLength(200)
  title!: string;

  @IsArray()
  @ArrayMinSize(1)
  @ArrayMaxSize(100)
  @IsUUID(undefined, { each: true })
  memberUserIds!: string[];

  @IsOptional()
  @IsUUID()
  localGovernmentId?: string;
}

export class AddMembersBody {
  @IsArray()
  @ArrayMinSize(1)
  @ArrayMaxSize(100)
  @IsUUID(undefined, { each: true })
  memberUserIds!: string[];
}

export class MuteConversationBody {
  /**
   * When to un-mute. Omitted means un-mute now — an explicit timestamp rather
   * than a duration so the client's clock never decides when silence ends.
   */
  @IsOptional()
  @IsISO8601()
  until?: string;
}

export class ListMessagesQuery {
  /** ISO timestamp of the oldest message already held; returns the page before it. */
  @IsOptional()
  @IsISO8601()
  cursor?: string;
}

/** The states a client may report a call has reached. */
export const CALL_STATUS_VALUES = [
  'ringing',
  'active',
  'completed',
  'missed',
  'declined',
  'failed',
] as const;

export type CallStatusValue = (typeof CALL_STATUS_VALUES)[number];

export class CallStatusBody {
  @IsIn(CALL_STATUS_VALUES)
  status!: CallStatusValue;

  /** Twilio's identifier for the call, once the browser knows it. */
  @IsOptional()
  @IsString()
  @MaxLength(64)
  providerCallSid?: string;
}
